import { useEffect, useMemo, useState } from 'react'

import {
  FeaturedQuote,
  pickFeaturedIndex,
  QuoteCard,
  QuoteItem,
} from '@/components/quick-study-results/QuickStudyOpenEndCard'

import { StudyDocQuote, studyDocQuoteToItem } from './enrichStudyDocQuote'
import {
  MosaicPickerLookup,
  resolveMosaicPickerImages,
} from './PortraitMosaicStrip/resolveMosaicPickerImages'
import { StudyDocQuotesPrintDigest } from './StudyDocPrintDigest'

import './studyDocQuotes.css'

export const STUDY_DOC_QUOTE_PREVIEW = 6

interface StudyDocQuoteEntry {
  id: string
  quote: StudyDocQuote
}

interface StudyDocQuotesGalleryProps {
  entries: Array<StudyDocQuoteEntry>
  /** Slim picker population for quote avatars missing imageUrl. Reuses mosaic resolver. */
  populationId?: string
}

function missingPortraitLookups(entries: Array<StudyDocQuoteEntry>): Array<MosaicPickerLookup> {
  const lookups: Array<MosaicPickerLookup> = []
  const seen = new Set<string>()

  for (const entry of entries) {
    const personaId = (entry.quote.personaId || '').trim()
    if (!personaId || seen.has(personaId)) {
      continue
    }
    if ((entry.quote.imageUrl || '').trim()) {
      continue
    }
    seen.add(personaId)
    lookups.push({
      personaId,
      name: (entry.quote.name || entry.quote.attribution || '').trim(),
    })
  }

  return lookups
}

export function StudyDocQuotesGallery(props: StudyDocQuotesGalleryProps) {
  const { entries, populationId } = props

  const [expanded, setExpanded] = useState(false)
  const [fetchedPickerImages, setFetchedPickerImages] = useState<Record<string, string>>({})

  const missingLookups = useMemo(() => missingPortraitLookups(entries), [entries])
  const missingPickerKey = useMemo(
    () =>
      missingLookups
        .map((lookup) => lookup.personaId)
        .sort()
        .join(','),
    [missingLookups],
  )

  useEffect(() => {
    if (!populationId || missingLookups.length === 0) {
      setFetchedPickerImages({})
      return
    }

    let cancelled = false

    void resolveMosaicPickerImages(populationId, missingLookups).then((next) => {
      if (!cancelled) {
        setFetchedPickerImages(next)
      }
    })

    return () => {
      cancelled = true
    }
    // Keyed on `missingPickerKey` (stable sorted ids) rather than `missingLookups`
    // identity — same pattern as PortraitMosaicStrip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populationId, missingPickerKey])

  const items = useMemo(
    () =>
      entries.map((entry) => {
        const item = studyDocQuoteToItem(entry.quote, entry.id)
        if (item.imageUrl) {
          return item
        }
        const personaId = (entry.quote.personaId || '').trim()
        const pickerUrl = personaId ? fetchedPickerImages[personaId] : undefined
        if (!pickerUrl) {
          return item
        }
        return { ...item, imageUrl: pickerUrl }
      }),
    [entries, fetchedPickerImages],
  )
  const featuredIndex = useMemo(() => pickFeaturedIndex(items), [items])
  const featured = featuredIndex >= 0 ? items[featuredIndex] : undefined
  const rest = items.filter((_, index) => index !== featuredIndex)
  const hasMore = rest.length > STUDY_DOC_QUOTE_PREVIEW

  return (
    <div className="space-y-4" data-testid="study-doc-quotes-gallery">
      <div className="print:hidden" data-print-hide="" data-testid="study-doc-quotes-gallery-live">
        {featured ? (
          <div data-testid="study-doc-quote-embed" data-quote-id={featured.key}>
            <FeaturedQuote item={featured} />
          </div>
        ) : null}
        {rest.length > 0 ? (
          <StudyDocQuotesGrid
            items={rest}
            expanded={expanded}
            hasMore={hasMore}
            onToggle={() => setExpanded((current) => !current)}
          />
        ) : null}
      </div>
      <StudyDocQuotesPrintDigest items={items} featuredIndex={featuredIndex} />
    </div>
  )
}

function StudyDocQuotesGrid(props: {
  items: Array<QuoteItem>
  expanded: boolean
  hasMore: boolean
  onToggle: () => void
}) {
  const { items, expanded, hasMore, onToggle } = props

  return (
    <div className="space-y-2" data-testid="study-doc-quotes-grid">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.map((item, index) => {
          const isExtra = hasMore && index >= STUDY_DOC_QUOTE_PREVIEW

          return (
            <div
              key={item.key}
              className={isExtra && !expanded ? 'study-doc-quote-extra' : undefined}
              data-testid="study-doc-quote-embed"
              data-quote-id={item.key}
            >
              <QuoteCard item={item} />
            </div>
          )
        })}
      </div>
      {hasMore ? (
        <div className="flex justify-center pt-1">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="rounded-full border border-border bg-card px-4 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            data-testid="study-doc-quote-load-toggle"
          >
            {expanded ? 'Load less' : 'Load all'}
          </button>
        </div>
      ) : null}
    </div>
  )
}
